// Weekly Challenges (Desafíos semanales) — isolated page at /shadow/desafios.
// Self-contained: no imports from existing Shadow shared modules other than
// Navbar/Footer/AuthContext/LanguageContext/lib/api.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Trophy, Flame, Clock, Coins, Sparkles, Loader2,
  Award, Calendar, CheckCircle2, History as HistoryIcon, Target,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tCh, pickLocalized, LEVEL_LABEL_KEY } from "../i18n/shadow-challenges";

const KEY_ICON = { publish: Target, reactions: Flame, correct: Trophy, referrals: Sparkles };
const KEY_CTA  = {
  publish:   { key: "cta_publish",   to: "/shadow" },
  reactions: { key: "cta_reactions", to: "/shadow/feed" },
  correct:   { key: "cta_correct",   to: "/shadow" },
  referrals: { key: "cta_referrals", to: "/" },
};
const ACCENT = {
  publish:   { border: "border-emerald-400/30", bg: "bg-emerald-400/[0.05]", text: "text-emerald-200", bar: "from-emerald-400 to-emerald-500" },
  reactions: { border: "border-rose-400/30",    bg: "bg-rose-400/[0.05]",    text: "text-rose-200",    bar: "from-rose-400 to-rose-500" },
  correct:   { border: "border-amber-400/30",   bg: "bg-amber-400/[0.05]",   text: "text-amber-200",   bar: "from-amber-400 to-amber-500" },
  referrals: { border: "border-cyan-400/30",    bg: "bg-cyan-400/[0.05]",    text: "text-cyan-200",    bar: "from-cyan-400 to-cyan-500" },
};

export default function ShadowChallengesPage() {
  const { user, token, openLogin } = useAuth();
  const { lang } = useLanguage();

  const [data, setData] = useState(null);
  const [history, setHistory] = useState({ history: [], xp_total: 0, level: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const load = async () => {
      try {
        const [d, h] = await Promise.all([
          fetch(`${API}/shadow/challenges`, { credentials: "include", headers }).then(safeJson),
          user
            ? fetch(`${API}/shadow/challenges/history?limit=20`, { credentials: "include", headers }).then(safeJson)
            : Promise.resolve(null),
        ]);
        if (!mounted) return;
        if (d) setData(d);
        if (h) setHistory(h);
      } catch (e) {
        if (mounted) setErr(tCh(lang, "err_load"));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [user, token, lang]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="challenges-page">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 pt-24 pb-12 md:px-6">
        <Link
          to="/shadow"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-amber-300"
          data-testid="challenges-back"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {tCh(lang, "nav_back")}
        </Link>

        <header className="mt-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300/80">
            {tCh(lang, "eyebrow")}
          </p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl">
            {tCh(lang, "title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">{tCh(lang, "sub")}</p>
        </header>

        {/* Top dashboard: countdown + earnings + level */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="challenges-stats">
          <CountdownPill end={data?.week_end} />
          <StatPill
            icon={Coins}
            label={tCh(lang, "earned_rbl")}
            value={data?.earned_this_week?.rbl ?? 0}
            color="text-amber-300"
            testId="challenges-earned-rbl"
          />
          <StatPill
            icon={Award}
            label={tCh(lang, "earned_xp")}
            value={data?.earned_this_week?.xp ?? 0}
            color="text-violet-300"
            testId="challenges-earned-xp"
          />
          <LevelPill xp={history.xp_total} level={history.level} lang={lang} />
        </div>

        {/* Challenge cards */}
        {loading && !data ? (
          <div className="mt-8 flex items-center gap-2 text-white/45">
            <Loader2 className="h-4 w-4 animate-spin" /> {tCh(lang, "loading")}
          </div>
        ) : err ? (
          <p className="mt-8 text-sm text-rose-300" data-testid="challenges-err">{err}</p>
        ) : (
          <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {(data?.challenges || []).map((ch) => (
              <ChallengeCard key={ch.id} ch={ch} lang={lang} />
            ))}
          </section>
        )}

        {/* Sign-in prompt for anonymous users */}
        {!user && (
          <div className="mt-8 rounded-2xl border border-amber-400/30 bg-amber-400/[0.04] p-5 text-center" data-testid="challenges-login-card">
            <p className="text-sm text-white/65">{tCh(lang, "login_sub")}</p>
            <button
              onClick={openLogin}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-5 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-black"
              data-testid="challenges-login-btn"
            >
              {tCh(lang, "login_btn")}
            </button>
          </div>
        )}

        {/* History */}
        {user && (
          <section className="mt-10" data-testid="challenges-history">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-white/75">
              <HistoryIcon className="h-4 w-4" /> {tCh(lang, "history_title")} ({history.history.length})
            </h2>
            {history.history.length === 0 ? (
              <p className="mt-3 text-xs text-white/45">{tCh(lang, "history_empty")}</p>
            ) : (
              <ul className="mt-3 divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025]">
                {history.history.map((h, i) => {
                  const ch = (data?.challenges || []).find((c) => c.key === h.challenge_key);
                  const title = ch ? pickLocalized(ch.title, lang) : h.challenge_key;
                  const Icon = KEY_ICON[h.challenge_key] || Trophy;
                  return (
                    <li key={i} className="flex items-center gap-3 px-4 py-3 text-xs" data-testid={`challenges-history-row-${i}`}>
                      <Icon className="h-4 w-4 text-amber-300/70" />
                      <span className="flex-1 font-bold text-white/85">{title}</span>
                      <span className="font-mono text-[10px] text-white/45">
                        {tCh(lang, "history_week", { w: h.week_id })}
                      </span>
                      <span className="font-mono text-[10px] text-emerald-300/80">
                        {h.completed_at ? new Date(h.completed_at).toLocaleDateString() : "—"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const ChallengeCard = ({ ch, lang }) => {
  const Icon = KEY_ICON[ch.key] || Trophy;
  const accent = ACCENT[ch.key] || ACCENT.publish;
  const current = ch.progress?.current_value ?? 0;
  const target = ch.target_value;
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const completed = !!ch.progress?.completed;
  const cta = KEY_CTA[ch.key];

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border ${accent.border} ${accent.bg} p-5 backdrop-blur transition-all hover:-translate-y-0.5`}
      data-testid={`challenge-card-${ch.key}`}
    >
      {completed && (
        <span
          className="absolute right-3 top-3 inline-flex animate-pulse items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/[0.12] px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em] text-emerald-200"
          data-testid={`challenge-completed-${ch.key}`}
        >
          <CheckCircle2 className="h-3 w-3" /> {tCh(lang, "completed_badge")}
        </span>
      )}
      <div className="flex items-start gap-3">
        <Icon className={`h-7 w-7 ${accent.text} shrink-0`} />
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-black text-white" data-testid={`challenge-title-${ch.key}`}>
            {pickLocalized(ch.title, lang)}
          </h3>
          <p className="mt-1 text-[12px] text-white/55">{pickLocalized(ch.description, lang)}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">
          <span>{tCh(lang, "progress_label")}</span>
          <span data-testid={`challenge-progress-${ch.key}`}>
            <span className="font-bold text-white/80">{current}</span> {tCh(lang, "of")} {target}
          </span>
        </div>
        <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-white/[0.06]">
          <div
            className={`h-full bg-gradient-to-r ${accent.bar} transition-all duration-500`}
            style={{ width: `${pct}%` }}
            data-testid={`challenge-bar-${ch.key}`}
          />
        </div>
      </div>

      {/* Rewards + CTA */}
      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-[0.16em]">
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/[0.08] px-2 py-0.5 text-amber-200">
            <Coins className="h-2.5 w-2.5" /> +{ch.reward_rbl} R₿L
          </span>
          <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-400/[0.08] px-2 py-0.5 text-violet-200">
            <Award className="h-2.5 w-2.5" /> +{ch.reward_xp} XP
          </span>
        </div>
        {!completed && cta && (
          <Link
            to={cta.to}
            className="shrink-0 text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 transition-colors hover:text-white"
            data-testid={`challenge-cta-${ch.key}`}
          >
            {tCh(lang, cta.key)}
          </Link>
        )}
      </div>
    </article>
  );
};

const CountdownPill = ({ end }) => {
  const { lang } = useLanguage();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let mounted = true;
    const tick = () => { if (mounted) setNow(Date.now()); };
    const t = setInterval(tick, 1000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const ms = useMemo(() => {
    if (!end) return 0;
    const t = new Date(end).getTime();
    return Math.max(0, t - now);
  }, [end, now]);

  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4 backdrop-blur" data-testid="challenges-countdown">
      <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">
        <Clock className="mr-1 inline h-3 w-3" /> {tCh(lang, "countdown_label")}
      </p>
      <p className="mt-1 font-mono text-2xl font-black text-amber-300">
        {d}{tCh(lang, "days")} {String(h).padStart(2, "0")}{tCh(lang, "hours")} {String(m).padStart(2, "0")}{tCh(lang, "minutes")} {String(s).padStart(2, "0")}{tCh(lang, "seconds")}
      </p>
    </div>
  );
};

const StatPill = ({ icon: Icon, label, value, color, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4 backdrop-blur" data-testid={testId}>
    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">
      <Icon className={`mr-1 inline h-3 w-3 ${color}`} /> {label}
    </p>
    <p className={`mt-1 font-mono text-2xl font-black ${color}`}>+{value}</p>
  </div>
);

const LevelPill = ({ xp, level, lang }) => {
  if (!level) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4 backdrop-blur" data-testid="challenges-level-anon">
        <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">
          <Award className="mr-1 inline h-3 w-3" /> {tCh(lang, "earned_level")}
        </p>
        <p className="mt-1 font-mono text-2xl font-black text-white/30">—</p>
      </div>
    );
  }
  const labelKey = LEVEL_LABEL_KEY[level.id] || "lvl_rookie";
  // Progress within current band
  const within = Math.max(0, xp - level.min);
  const span = Math.max(1, level.max - level.min);
  const pct = Math.min(100, Math.round((within / span) * 100));
  return (
    <div className="rounded-2xl border border-violet-400/30 bg-violet-400/[0.05] p-4 backdrop-blur" data-testid="challenges-level">
      <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-violet-200/80">
        <Award className="mr-1 inline h-3 w-3" /> {tCh(lang, "earned_level")}
      </p>
      <p className="mt-1 flex items-baseline gap-2">
        <span className="font-mono text-2xl font-black text-violet-200" data-testid="challenges-level-label">{tCh(lang, labelKey)}</span>
        <span className="font-mono text-[11px] text-white/55">{xp} XP</span>
      </p>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
        <div className="h-full bg-gradient-to-r from-violet-400 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
